// A table that sorts by its headings on a desktop, and becomes rows of a list on a phone: each row's title, a line
// under it, and what goes at its end.
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { cx } from './cx';
import { useMediaQuery } from './hooks/use-media-query';
import { CaretDownIcon, CaretRightIcon, CaretUpIcon } from './icons';
import s from './Layout.module.css';

export interface Column<T> {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** How rows compare when sorted by this column. */
  compare?: (a: T, b: T) => number;
  align?: 'start' | 'end';
  /** On a phone: the row's title, the line under it, or what goes at its end. Others aren't shown there. */
  phone?: 'title' | 'detail' | 'end';
}

export function DataTable<T>({
  label,
  columns,
  rows,
  rowKey,
  rowLink,
  sort: initial,
}: {
  /** What the table lists, read as its caption. */
  label: string;
  columns: ReadonlyArray<Column<T>>;
  rows: readonly T[];
  rowKey: (row: T) => string;
  /** Where a row goes, when it goes somewhere: its title becomes the link. */
  rowLink?: (row: T) => string;
  sort?: { key: string; descending?: boolean };
}) {
  const desktop = useMediaQuery('(min-width: 768px)');
  const [sort, setSort] = useState(initial ?? null);
  const sorted = useMemo(() => {
    const column = columns.find((c) => c.key === sort?.key);
    if (!column?.compare || !sort) {
      return rows;
    }
    const compare = column.compare;
    return [...rows].sort((a, b) => (sort.descending ? -compare(a, b) : compare(a, b)));
  }, [rows, columns, sort]);

  const titleColumn = columns.find((c) => c.phone === 'title') ?? columns[0];

  if (!desktop) {
    const detail = columns.filter((c) => c.phone === 'detail');
    const end = columns.filter((c) => c.phone === 'end');
    return (
      <ul className={s.phoneRows} role="list" aria-label={label}>
        {sorted.map((row) => {
          const link = rowLink?.(row);
          return (
            <li key={rowKey(row)}>
              <div className={s.phoneRow}>
                <span className={s.phoneTitle}>
                  {link ? (
                    <Link to={link} className={s.rowLink}>
                      {titleColumn?.cell(row)}
                    </Link>
                  ) : (
                    titleColumn?.cell(row)
                  )}
                </span>
                {detail.length > 0 && (
                  <span className={s.phoneDetail}>
                    {detail.map((c, i) => (
                      <span key={c.key}>
                        {i > 0 && ' · '}
                        {c.cell(row)}
                      </span>
                    ))}
                  </span>
                )}
                <span className={s.phoneEnd}>
                  {end.map((c) => (
                    <span key={c.key}>{c.cell(row)}</span>
                  ))}
                  {link && <CaretRightIcon size={16} weight="bold" aria-hidden="true" />}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className={s.tableWrap}>
      <table className={s.table}>
        <caption className="visually-hidden">{label}</caption>
        <thead>
          <tr>
            {columns.map((c) => {
              const on = sort?.key === c.key;
              return (
                <th
                  key={c.key}
                  scope="col"
                  className={cx(c.align === 'end' && s.end)}
                  aria-sort={on ? (sort?.descending ? 'descending' : 'ascending') : undefined}
                >
                  {c.compare ? (
                    <button
                      type="button"
                      className={s.sortButton}
                      onClick={() => setSort({ key: c.key, descending: on ? !sort?.descending : false })}
                    >
                      {c.header}
                      {on &&
                        (sort?.descending ? (
                          <CaretDownIcon size={12} weight="bold" aria-hidden="true" />
                        ) : (
                          <CaretUpIcon size={12} weight="bold" aria-hidden="true" />
                        ))}
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const link = rowLink?.(row);
            return (
              <tr key={rowKey(row)} className={cx(link && s.linked)}>
                {columns.map((c) => (
                  <td key={c.key} className={cx(c.align === 'end' && s.end)}>
                    {link && c === titleColumn ? (
                      <Link to={link} className={s.rowLink}>
                        {c.cell(row)}
                      </Link>
                    ) : (
                      c.cell(row)
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
